import { useAuth } from '@bb/session-manager';
import { Button } from '@rocket/ui';
import { useLocation } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';

import { CONTRACT_EXPORT_LOCK_ENDPOINT } from '../../endpoints.config';
import { submitContractExportWebhook } from '../../requests/general';
import { forceSaveRequest, logDocumentExport } from '../../utils/OnlyOfficeUtils';
import OpenExportDialog from './OpenExportDialog';

// biome-ignore lint/suspicious/noExplicitAny: legacy untyped redux state + OnlyOffice window globals
type AnyData = any;

const ExportButton = () => {
  const onlyOfficeUrl = useSelector(
    (state: AnyData) => state.OnlyOffice.onlyOfficeUrl,
    shallowEqual
  );

  const urlSearchParams = new URLSearchParams(useLocation().searchStr || '');
  const contractId = urlSearchParams.get('contract');

  const [openExportDialog, setOpenExportDialog] = useState(false);
  const [isDisabled, setIsDisabled] = useState(true);
  const [intervalId, setIntervalId] = useState<ReturnType<typeof setInterval>>();
  const [lockReason, setLockReason] = useState<string | null>(null);

  const { config, bbAxios } = useAuth() as AnyData;
  const { exportWebhook = {} } = (config as AnyData) || {};

  // Approvals can lock export until legal closes the review. The backend owns the decision (it also
  // exempts legal roles), so we only render whatever reason it hands back. Any failure leaves export
  // open — a review nobody can see is better than a contract nobody can export.
  useEffect(() => {
    if (!contractId) {
      setLockReason(null);
      return;
    }
    bbAxios
      .get(CONTRACT_EXPORT_LOCK_ENDPOINT(contractId))
      .then(({ data }: AnyData) => setLockReason(data?.locked ? data.reason : null))
      .catch(() => setLockReason(null));
  }, [bbAxios, contractId]);

  const handleExport = async () => {
    setOpenExportDialog(false);
    try {
      // Await the export log so lastExported is persisted before the user
      // navigates away. Without this the fire-and-forget POST could be
      // cancelled by navigation, leaving lastExported unset.
      await logDocumentExport(contractId);
    } catch (e) {
      // Even if the log call failed, still allow the download so the user
      // gets their file. The checkmark may be missing until next export.
      console.error('Export log failed, proceeding with download:', e);
    }

    if (exportWebhook.enabled !== false) {
      submitContractExportWebhook(contractId)
        .then(() => console.info('Contract export webhook submitted'))
        .catch((err) =>
          console.warn('Contract export webhook failed (non-blocking):', err.message)
        );
    }

    try {
      (window as AnyData).DocsAPICopy.downloadAs();
    } catch (e) {
      console.error('Download failed:', e);
    }
  };

  const handleOpenExport = () => {
    if (lockReason) {
      return;
    }

    // The signing key is required: document-server rejects an unsigned forcesave, so
    // without it the flush never happens and the export can miss the latest edits.
    forceSaveRequest(onlyOfficeUrl, contractId, (config as AnyData)?.onlyofficeTokenKey)
      .then((resp) => console.info('Received response from force save request:', resp))
      .catch((err) => console.error('Failed to perform force save request with error:', err));

    setOpenExportDialog(true);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy dep array preserved verbatim (intervalId intentionally excluded)
  useEffect(() => {
    if (isDisabled) {
      const newIntervalId = setInterval(() => {
        setIsDisabled(typeof (window as AnyData).DocsAPI === 'undefined');
      }, 1000);
      setIntervalId(newIntervalId);
    } else {
      setIntervalId(undefined);
      clearInterval(intervalId);
    }
  }, [isDisabled]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy dep array preserved verbatim (intervalId intentionally excluded)
  useEffect(() => () => clearInterval(intervalId), [isDisabled]);

  return (
    <>
      <Button
        variant="solid"
        colorPalette={lockReason ? 'orange' : 'gray'}
        disabled={isDisabled || Boolean(lockReason)}
        title={lockReason || undefined}
        onClick={handleOpenExport}
      >
        {lockReason ? 'Export Locked' : 'Export Contract'}
      </Button>
      <OpenExportDialog
        open={openExportDialog}
        handleExport={handleExport}
        handleClose={() => setOpenExportDialog(false)}
      />
    </>
  );
};

export default ExportButton;
